import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import "../../../styles/pageStyles/applicationListing/inlineFilterPopup.css";
import { parse, isValid, format } from "date-fns";
import CustomDatePickerInput from "../../common/CustomDatePickerInput";

/**
 * This component renders a popup filter UI for table columns.
 * It supports 3 modes:
 * 1. Text filter       → contains / startswith / endswith / zip
 * 2. Boolean filter    → Yes / No
 * 3. Date range filter → From / To / Created On
 * 4. Update Date → Update Date On
 * NOTE:
 * - Parent handles actual filtering logic / API calls
 */

const InlineFilterPopup = ({
  fieldKey,
  onApply,
  onClear,
  onClose,
  defaultValues = {},
  onPendingChange,
  fieldType = "text", // fieldType = "text" | "boolean" | "daterange" | "dateon" | "numericrange" | "exactcontains"
  textFilterVariant = "default", // "default" | "full" (includes endswith)
  hideExactOn = false, // hide "Created On" / exact date for range filters (e.g. homeowner approval date)
  showHasDateBoolean = false, // Purchase Date: Yes/No → has date / isnull
  showNotAvailableOption = false,
}) => {
  // Reference used for detecting outside click (to close popup)
  const popupRef = useRef();
  const anchorRef = useRef();
  const hasUserEdited = useRef(false);
  const [popupPos, setPopupPos] = useState({
    top: 0,
    bottom: null,
    left: 0,
    maxHeight: 0,
    placement: "below",
    ready: false,
  });

  // ── text mode state ────
  const [filters, setFilters] = useState({
    contains: "",
    startswith: "",
    endswith: "",
    zip_code: "",
    exact: "",
    gte: "",
    lte: "",
    ...defaultValues,
  });

  const [appNumberContains, setAppNumberContains] = useState(
    defaultValues?.contains || "",
  );


  // ── boolean mode state ─────
  const [boolValue, setBoolValue] = useState(
    defaultValues?.value !== undefined ? defaultValues.value : undefined,
  );
  const [isNullSelected, setIsNullSelected] = useState(
    defaultValues?.is_null === true,
  );

  // ── daterange mode state ────
  const fromRef = useRef(null);
  const toRef = useRef(null);
  const onRef = useRef(null);

  // Display values (MM/DD/YYYY format for UI)
  const [fromDisplay, setFromDisplay] = useState(
    defaultValues?.from
      ? (() => {
        const [y, m, d] = defaultValues.from.split("-");
        return `${m}/${d}/${y}`;
      })()
      : "",
  );
  const [toDisplay, setToDisplay] = useState(
    defaultValues?.to
      ? (() => {
        const [y, m, d] = defaultValues.to.split("-");
        return `${m}/${d}/${y}`;
      })()
      : "",
  );

  const [onDisplay, setOnDisplay] = useState(
    defaultValues?.on
      ? (() => {
        const [y, m, d] = defaultValues.on.split("-");
        return `${m}/${d}/${y}`;
      })()
      : "",
  );

  // ISO values (YYYY-MM-DD → used for backend/API)
  const [fromISO, setFromISO] = useState(defaultValues?.from || "");
  const [toISO, setToISO] = useState(defaultValues?.to || "");
  const [onISO, setOnISO] = useState(defaultValues?.on || "");

  // Selected Date objects (used for date picker)
  const [selectedFrom, setSelectedFrom] = useState(null);
  const [selectedTo, setSelectedTo] = useState(null);
  const [selectedOn, setSelectedOn] = useState(null);

  // Purchase Date exists Yes/No (maps to isnull=false/true)
  const [hasDateValue, setHasDateValue] = useState(
    defaultValues?.has_date !== undefined ? defaultValues.has_date : undefined,
  );

  // Validation error message for date inputs
  const [dateRangeError, setDateRangeError] = useState("");

  const validateDateRange = (from, to, on) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (on) {
      if (new Date(on) > today) return { valid: false };
      return { valid: true, result: { on } };
    }
    if (!from && !to) return { valid: false };
    if (from && !to) return { valid: false };
    if (!from && to) return { valid: false };
    if (new Date(from) > today) return { valid: false };
    if (new Date(to) > today) return { valid: false };
    if (new Date(to) < new Date(from)) return { valid: false };

    return { valid: true, result: { from, to } };
  };

  // Keep Has Purchase Date Yes/No when From/To pending updates
  const emitDateRangePending = (rangeResult, partial = {}) => {
    if (!onPendingChange) return;
    const pending = { ...(rangeResult || {}), ...partial };
    // Drop empty keys from partial overrides
    Object.keys(pending).forEach((k) => {
      if (pending[k] === undefined || pending[k] === "") delete pending[k];
    });
    if (showHasDateBoolean && hasDateValue !== undefined) {
      pending.has_date = hasDateValue;
    }
    onPendingChange(
      fieldKey,
      Object.keys(pending).length ? pending : null,
    );
  };

  useEffect(() => {
    if (fieldType === "boolean") {
      setBoolValue(
        defaultValues?.value !== undefined ? defaultValues.value : undefined,
      );
      setIsNullSelected(defaultValues?.is_null === true);
    } else if (fieldType === "daterange") {
      if (!hasUserEdited.current) {
        if (defaultValues?.from) {
          if (/^\d{4}-\d{2}-\d{2}$/.test(defaultValues.from)) {
            const [y, m, d] = defaultValues.from.split("-");
            setFromDisplay(`${m}/${d}/${y}`);
            setFromISO(defaultValues.from);
          } else {
            setFromDisplay(defaultValues.from);
          }
        }
        if (defaultValues?.to) {
          if (/^\d{4}-\d{2}-\d{2}$/.test(defaultValues.to)) {
            const [y, m, d] = defaultValues.to.split("-");
            setToDisplay(`${m}/${d}/${y}`);
            setToISO(defaultValues.to);
          } else {
            setToDisplay(defaultValues.to);
          }
        }
        if (defaultValues?.on) {
          if (/^\d{4}-\d{2}-\d{2}$/.test(defaultValues.on)) {
            const [y, m, d] = defaultValues.on.split("-");
            setOnDisplay(`${m}/${d}/${y}`);
            setOnISO(defaultValues.on);
          } else {
            setOnDisplay(defaultValues.on);
          }
        }
        if (defaultValues?.has_date !== undefined) {
          setHasDateValue(defaultValues.has_date);
        } else if (!hasUserEdited.current) {
          setHasDateValue(undefined);
        }
      }
    } else if (fieldType === "appnumber") {
      if (!hasUserEdited.current) {
        setAppNumberContains(defaultValues?.contains || "");
      }
    } else if (fieldType === "dateon") {
      if (!hasUserEdited.current) {
        if (defaultValues?.on) {
          if (/^\d{4}-\d{2}-\d{2}$/.test(defaultValues.on)) {
            const [y, m, d] = defaultValues.on.split("-");
            setOnDisplay(`${m}/${d}/${y}`);
            setOnISO(defaultValues.on);
          } else {
            setOnDisplay(defaultValues.on);
          }
        }
      }
    } else if (!hasUserEdited.current) {
      setFilters((prev) => ({
        contains: "",
        startswith: "",
        endswith: "",
        zip_code: "",
        exact: "",
        gte: "",
        lte: "",
        ...defaultValues,
      }));
    }
  }, [defaultValues, fieldType]);

  // Close on outside click (ignore portaled calendar / month-year menus)
  useEffect(() => {
    const isInsideDatePickerUi = (target) => {
      if (!target?.closest) return false;
      return Boolean(
        target.closest(".react-datepicker") ||
        target.closest(".react-datepicker-popper") ||
        target.closest(".react-datepicker__portal") ||
        target.closest(".MuiPopover-root") ||
        target.closest(".MuiMenu-root") ||
        target.closest(".MuiModal-root") ||
        target.closest("[role='listbox']") ||
        target.closest(".MuiPickersPopper-root"),
      );
    };

    const handleClickOutside = (e) => {
      if (!popupRef.current || popupRef.current.contains(e.target)) return;
      // Keep open when clicking the filter icon that toggles this popup
      if (e.target.closest?.(".MuiSvgIcon-root")) return;
      // Keep open when interacting with portaled calendar / month-year dropdowns
      if (isInsideDatePickerUi(e.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Portal popup: follow column on scroll; close popup when user scrolls
  useLayoutEffect(() => {
    const updatePosition = () => {
      if (!anchorRef.current) return;
      const cell =
        anchorRef.current.closest("th, td") || anchorRef.current.parentElement;
      if (!cell) return;

      const icon = cell.querySelector(".MuiSvgIcon-root");
      const rect = (icon || cell).getBoundingClientRect();
      const table = cell.closest("table");
      const container = cell.closest(".MuiTableContainer-root");
      const containerRect = container?.getBoundingClientRect();

      const width =
        fieldType === "boolean"
          ? 160
          : fieldType === "daterange" || fieldType === "dateon"
            ? 280
            : 230;

      // Right edge of sticky headers to the LEFT of this column (not including itself)
      let stickyRight = containerRect?.left ?? 0;
      if (table) {
        table.querySelectorAll("thead th").forEach((th) => {
          if (th === cell) return;
          if (getComputedStyle(th).position === "sticky") {
            stickyRight = Math.max(
              stickyRight,
              th.getBoundingClientRect().right,
            );
          }
        });
      }

      const viewLeft = stickyRight;
      const viewRight = containerRect?.right ?? window.innerWidth;
      const viewTop = containerRect?.top ?? 0;
      const viewBottom = containerRect?.bottom ?? window.innerHeight;

      // Column scrolled under sticky headers or out of the table viewport → hide with it
      const isHiddenUnderSticky = rect.right <= viewLeft + 2;
      const isOffToRight = rect.left >= viewRight - 2;
      const isOffVertically =
        rect.bottom <= viewTop + 2 || rect.top >= viewBottom - 2;

      if (isHiddenUnderSticky || isOffToRight || isOffVertically) {
        setPopupPos((prev) => ({ ...prev, ready: false }));
        return;
      }

      // Follow the column icon horizontally; use the header cell for vertical placement
      const datePopupLeftOffset =
        fieldType === "daterange" || fieldType === "dateon" ? 36 : 0;
      let left = rect.left - datePopupLeftOffset;
      left = Math.max(viewLeft + 4, left);
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - width - 8);
      }

      // Prefer below whenever there is usable room. Only flip above when
      // space below cannot fit the popup (and above is clearly better).
      const estimatedHeight =
        fieldType === "boolean"
          ? 130
          : fieldType === "dateon"
            ? 190
            : fieldType === "daterange"
              ? 300
              : fieldType === "numericrange"
                ? 210
                : fieldType === "appnumber"
                  ? 180
                  : 220;

      const gap = 4;
      const viewportPadding = 12;
      const cellRect = cell.getBoundingClientRect();
      const spaceBelow =
        window.innerHeight - cellRect.bottom - gap - viewportPadding;
      const spaceAbove = cellRect.top - gap - viewportPadding;

      const measuredHeight = popupRef.current?.offsetHeight || 0;
      const neededHeight =
        measuredHeight > 40 ? measuredHeight : estimatedHeight;

      // Keep below if it fits, or if below still has a usable scroll area
      const minUsableBelow = 180;
      const openAbove =
        spaceBelow < neededHeight &&
        spaceBelow < minUsableBelow &&
        spaceAbove > spaceBelow;

      if (openAbove) {
        setPopupPos({
          top: "auto",
          bottom: window.innerHeight - cellRect.top + gap,
          left,
          maxHeight: Math.max(120, spaceAbove),
          placement: "above",
          ready: true,
        });
      } else {
        const top = cellRect.bottom + gap;
        setPopupPos({
          top,
          bottom: null,
          left,
          maxHeight: Math.max(120, window.innerHeight - top - viewportPadding),
          placement: "below",
          ready: true,
        });
      }
    };

    updatePosition();
    // Second pass after paint so real popup height is used for flip decisions
    const rafId = requestAnimationFrame(() => updatePosition());

    const isInsideDatePickerUi = (target) => {
      if (!target?.closest) return false;
      return Boolean(
        target.closest(".react-datepicker") ||
        target.closest(".react-datepicker-popper") ||
        target.closest(".react-datepicker__portal") ||
        target.closest(".MuiPopover-root") ||
        target.closest(".MuiMenu-root") ||
        target.closest(".MuiModal-root") ||
        target.closest("[role='listbox']"),
      );
    };

    const handleScroll = (e) => {
      if (popupRef.current && popupRef.current.contains(e.target)) return;
      // Scrolling month/year dropdown must not close the filter popup
      if (isInsideDatePickerUi(e.target)) return;
      onClose();
    };

    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [fieldType, onClose]);

  const renderPortaledPopup = (className, children) => (
    <>
      <span
        ref={anchorRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          bottom: 0,
          left: 0,
          pointerEvents: "none",
        }}
      />
      {createPortal(
        <div
          ref={popupRef}
          className={`${className} inline-filter-popup-portaled`}
          style={{
            position: "fixed",
            top: popupPos.placement === "above" ? "auto" : popupPos.top,
            bottom:
              popupPos.placement === "above" ? popupPos.bottom : "auto",
            left: popupPos.left,
            right: "auto",
            zIndex: 14000,
            maxHeight: popupPos.maxHeight || undefined,
            overflowY: "auto",
            overflowX: "hidden",
            visibility: popupPos.ready ? "visible" : "hidden",
            pointerEvents: popupPos.ready ? "auto" : "none",
          }}
        >
          {children}
        </div>,
        document.body,
      )}
    </>
  );

  // ─────
  // DATE INPUT HANDLERS
  // These ensure:
  // - Proper MM/DD/YYYY formatting
  // - Validation
  // - Mutual exclusion between "range" and "on"

  const formatDateInput = (value) => {
    let val = String(value ?? "");

    // 1. Allow only digits and slashes
    val = val.replace(/[^\d/]/g, "");

    // 2 & 3. No more than 2 slashes
    const slashCount = (val.match(/\//g) || []).length;
    if (slashCount > 2) {
      val = val.split("/").slice(0, 3).join("/");
    }

    // 4. Auto-insert slashes
    const digitsOnly = val.replace(/\D/g, "");
    if (!val.includes("/") && digitsOnly.length > 2) {
      val = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
    }
    if ((val.match(/\//g) || []).length === 1 && digitsOnly.length > 4) {
      val = `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4, 8)}`;
    }

    return val.slice(0, 10);
  };

const maskDateInput = (e, prevValue = "") => {
  const input = e.target;
  const raw = input.value;
  const formatted = formatDateInput(raw);

  const slashRemoved =
    raw.length < prevValue.length && formatted.length > raw.length;

  if (slashRemoved) {
    requestAnimationFrame(() => {
      if (input && document.activeElement === input) {
        input.setSelectionRange(formatted.length, formatted.length);
      }
    });
  }
  return formatted;
};

  const clearOnDate = () => {
    setOnDisplay("");
    setOnISO("");
    setSelectedOn(null);
  };

  const handleFromInput = (e) => {
    hasUserEdited.current = true;
    clearOnDate();
    const val = maskDateInput(e,fromDisplay);
    setFromDisplay(val);
    let newFromISO = "";
    if (val.length === 10) {
      const parsed = parse(val, "MM/dd/yyyy", new Date());
      if (isValid(parsed)) {
        newFromISO = format(parsed, "yyyy-MM-dd");
      }
    }
    setSelectedFrom(newFromISO ? parse(val, "MM/dd/yyyy", new Date()) : null);
    setFromISO(newFromISO);
    const { valid, result } = validateDateRange(newFromISO, toISO, "");
    emitDateRangePending(valid ? result : null, {
      from: newFromISO || undefined,
      to: toISO || undefined,
    });
    if (!valid && !val && !toISO && hasDateValue === undefined) onClear(fieldKey);
  };

  const handleToInput = (e) => {
    hasUserEdited.current = true;
    clearOnDate();
    const val = maskDateInput(e, toDisplay);
    setToDisplay(val);

    let newToISO = "";
    if (val.length === 10) {
      const parsed = parse(val, "MM/dd/yyyy", new Date());
      if (isValid(parsed)) {
        newToISO = format(parsed, "yyyy-MM-dd");
      }
    }
    setSelectedTo(newToISO ? parse(val, "MM/dd/yyyy", new Date()) : null);
    setToISO(newToISO);

    const { valid, result } = validateDateRange(fromISO, newToISO, "");
    emitDateRangePending(valid ? result : null, {
      from: fromISO || undefined,
      to: newToISO || undefined,
    });
    if (!valid && !val && !fromISO && hasDateValue === undefined) onClear(fieldKey);
  };

  const handleOnInput = (e) => {
    hasUserEdited.current = true;
    setFromDisplay(""); setFromISO(""); setSelectedFrom(null);
    setToDisplay(""); setToISO(""); setSelectedTo(null);
    setDateRangeError("");

    const val = maskDateInput(e, onDisplay);
    setOnDisplay(val);

    let newOnISO = "";
    if (val.length === 10) {
      const parsed = parse(val, "MM/dd/yyyy", new Date());
      if (isValid(parsed)) {
        newOnISO = format(parsed, "yyyy-MM-dd");
      }
    }
    setSelectedOn(newOnISO ? parse(val, "MM/dd/yyyy", new Date()) : null);
    setOnISO(newOnISO);

    const { valid, result } = validateDateRange("", "", newOnISO);
    if (valid) {
      if (onPendingChange) onPendingChange(fieldKey, result);
    } else {
      if (onPendingChange) onPendingChange(fieldKey, null);
      if (!val) onClear(fieldKey);
    }
  };

  const handleFromCalendar = (date) => {
    hasUserEdited.current = true;
    const iso = format(date, "yyyy-MM-dd");
    setSelectedFrom(date);
    setFromDisplay(format(date, "MM/dd/yyyy"));
    setFromISO(iso);
    setDateRangeError("");
    setOnDisplay(""); setOnISO(""); setSelectedOn(null);

    const { valid, result } = validateDateRange(iso, toISO, "");
    emitDateRangePending(valid ? result : null, {
      from: iso,
      to: toISO || undefined,
    });
  };

  const handleToCalendar = (date) => {
    hasUserEdited.current = true;
    const iso = format(date, "yyyy-MM-dd");
    setSelectedTo(date);
    setToDisplay(format(date, "MM/dd/yyyy"));
    setToISO(iso);
    setDateRangeError("");
    setOnDisplay(""); setOnISO(""); setSelectedOn(null);

    const { valid, result } = validateDateRange(fromISO, iso, "");
    emitDateRangePending(valid ? result : null, {
      from: fromISO || undefined,
      to: iso,
    });
  };

  const handleOnCalendar = (date) => {
    hasUserEdited.current = true;
    const iso = format(date, "yyyy-MM-dd");
    setSelectedOn(date);
    setOnDisplay(format(date, "MM/dd/yyyy"));
    setOnISO(iso);
    setDateRangeError("");
    setFromDisplay(""); setFromISO(""); setSelectedFrom(null);
    setToDisplay(""); setToISO(""); setSelectedTo(null);

    const { valid, result } = validateDateRange("", "", iso);
    if (onPendingChange) {
      onPendingChange(fieldKey, valid ? result : null);
    }
  };

  const sanitizeNumericInput = (value) => {
    let cleaned = String(value ?? "").replace(/[^\d.]/g, "");
    const firstDot = cleaned.indexOf(".");
    if (firstDot !== -1) {
      cleaned =
        cleaned.slice(0, firstDot + 1) +
        cleaned.slice(firstDot + 1).replace(/\./g, "");
    }
    return cleaned;
  };

  const sanitizeDigitsOnly = (value) => String(value ?? "").replace(/\D/g, "");

  const handleChange = (key, value) => {
    hasUserEdited.current = true;
    const updated = { ...filters, [key]: value };
    setFilters(updated);

    const nonEmpty = Object.fromEntries(
      Object.entries(updated).filter(([, v]) => v?.trim()),
    );
    const hasValues = Object.keys(nonEmpty).length > 0;

    if (onPendingChange) {
      onPendingChange(fieldKey, hasValues ? nonEmpty : null);
    }
    if (!hasValues) {
      onClear(fieldKey);   // NEW
    }
  };

  const handleApply = () => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (fieldType === "numericrange") {
      const result = {};
      const gte = sanitizeNumericInput(filters.gte).trim();
      const lte = sanitizeNumericInput(filters.lte).trim();
      if (gte) result.gte = gte;
      if (lte) result.lte = lte;
      if (Object.keys(result).length === 0) return;
      onApply(fieldKey, result);
      onClose();
      return;
    }
    if (fieldType === "exactcontains") {
      const result = {};
      const exactRaw = filters.exact?.trim() || "";
      const containsRaw = filters.contains?.trim() || "";
      const exact =
        fieldKey === "partner_loan_number"
          ? sanitizeDigitsOnly(exactRaw)
          : exactRaw;
      const contains =
        fieldKey === "partner_loan_number"
          ? sanitizeDigitsOnly(containsRaw)
          : containsRaw;
      if (exact) result.exact = exact;
      if (contains) result.contains = contains;
      if (Object.keys(result).length === 0) return;
      onApply(fieldKey, result);
      onClose();
      return;
    }
    if (fieldType === "boolean") {
      if (boolValue !== undefined) {
        onApply(fieldKey, { value: boolValue });
      } else if (isNullSelected) {
        onApply(fieldKey, { is_null: true });
      }
      onClose();
      return;
    }
    if (fieldType === "dateon") {
      if (!onISO) {
        setDateRangeError("Updated date is invalid.");
        return;
      }
      if (new Date(onISO) > today) {
        setDateRangeError("Future dates are not allowed.");
        return;
      }
      onApply(fieldKey, { on: onISO });
      onClose();
      return;
    }
    if (fieldType === "daterange") {
      if (fromDisplay && !fromISO) {
        setDateRangeError("From date is invalid.");
        return;
      }
      if (toDisplay && !toISO) {
        setDateRangeError("To date is invalid.");
        return;
      }
      if (!hideExactOn && onDisplay && !onISO) {
        setDateRangeError("Created date is invalid.");
        return;
      }
      if (!fromISO && !toISO && !onISO && hasDateValue === undefined) {
        setDateRangeError(
          showHasDateBoolean
            ? "Please enter a date range or select Yes/No."
            : "Please enter at least one date.",
        );
        return;
      }

      if (!hideExactOn && onISO) {
        if (new Date(onISO) > today) {
          setDateRangeError("Future dates are not allowed.");
          return;
        }
        onApply(fieldKey, { on: onISO });
        onClose();
        return;
      }
      if (fromISO && !toISO) {
        setDateRangeError("Please enter the To date.");
        return;
      }
      if (!fromISO && toISO) {
        setDateRangeError("Please enter the From date.");
        return;
      }
      if (fromISO && new Date(fromISO) > today) {
        setDateRangeError("From date cannot be a future date.");
        return;
      }
      if (toISO && new Date(toISO) > today) {
        setDateRangeError("To date cannot be a future date.");
        return;
      }
      if (fromISO && toISO && new Date(toISO) < new Date(fromISO)) {
        setDateRangeError("To date cannot be earlier than From date.");
        return;
      }
      const result = {};
      if (fromISO && toISO) {
        result.from = fromISO;
        result.to = toISO;
      }
      if (showHasDateBoolean && hasDateValue !== undefined) {
        result.has_date = hasDateValue;
      }
      if (Object.keys(result).length === 0) return;
      onApply(fieldKey, result);
      onClose();
      return;
    }
    if (fieldType === "appnumber") {
      if (!appNumberContains.trim()) return;
      onApply(fieldKey, { contains: appNumberContains.trim() });
      onClose();
      return;
    }

    onApply(fieldKey, filters);
    onClose();
  };

  const handleClear = () => {
    if (fieldType === "boolean") {
      setBoolValue(undefined);
      setIsNullSelected(false);
      onClear(fieldKey);
      onClose();
      return;
    }
    if (fieldType === "dateon") {
      setOnDisplay("");
      setOnISO("");
      setSelectedOn(null);
      setDateRangeError("");
      onClear(fieldKey);
      onClose();
      return;
    }
    if (fieldType === "daterange") {
      setFromDisplay("");
      setToDisplay("");
      setOnDisplay("");
      setFromISO("");
      setToISO("");
      setOnISO("");
      setSelectedFrom(null);
      setSelectedTo(null);
      setSelectedOn(null);
      setHasDateValue(undefined);
      setDateRangeError("");
      onClear(fieldKey);
      onClose();
      return;
    }
    if (fieldType === "appnumber") {
      setAppNumberContains("");
      onClear(fieldKey);
      onClose();
      return;
    }
    if (fieldType === "numericrange" || fieldType === "exactcontains") {
      setFilters({
        contains: "",
        startswith: "",
        endswith: "",
        zip_code: "",
        exact: "",
        gte: "",
        lte: "",
      });
      onClear(fieldKey);
      onClose();
      return;
    }
    setFilters({
      contains: "",
      startswith: "",
      endswith: "",
      zip_code: "",
      exact: "",
      gte: "",
      lte: "",
    });
    onClear(fieldKey);
    onClose();
  }

  const hasAnyValue =
    fieldType === "boolean"
      ? boolValue !== undefined || isNullSelected
      : fieldType === "dateon"
        ? !!onDisplay
        : fieldType === "daterange"
          ? !!fromDisplay ||
          !!toDisplay ||
          (!hideExactOn && !!onDisplay) ||
          (showHasDateBoolean && hasDateValue !== undefined)
          : fieldType === "appnumber"
            ? appNumberContains.trim().length > 0
            : fieldType === "numericrange"
              ? !!filters.gte?.trim() || !!filters.lte?.trim()
              : fieldType === "exactcontains"
                ? !!filters.exact?.trim() || !!filters.contains?.trim()
                : Object.values(filters).some((v) => v?.trim());

  const isAddressField = fieldKey === "property_address";
  const isBorrowerField = fieldKey === "borrower_name";
  const isCxSpecialistField = fieldKey === "cx_specialist_name";
  const isStatusField = fieldKey === "application_status_name";

  // ── Boolean mode ────
  if (fieldType === "boolean") {
    return renderPortaledPopup(
      showNotAvailableOption
        ? "inline-filter-popup-checkbox inline-filter-popup-checkbox--wide"
        : "inline-filter-popup-checkbox",
      <>
        <div className="filter-field-checkbox">
          {[
            { label: "Yes", value: true },
            { label: "No", value: false },
          ].map(({ label, value }) => (
            <div className="filter-field" key={label}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={boolValue === value}
                  onChange={() => {
                    const newVal = boolValue === value ? undefined : value;
                    setBoolValue(newVal);
                    setIsNullSelected(false);
                    if (onPendingChange) {
                      onPendingChange(
                        fieldKey,
                        newVal !== undefined ? { value: newVal } : null,
                      );
                    }
                    if (newVal === undefined) {
                      onClear(fieldKey);   // NEW
                    }
                  }}
                />
                {label}
              </label>
            </div>
          ))}
          {showNotAvailableOption && (
            <div className="filter-field" key="Not Available">
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={isNullSelected}
                  onChange={() => {
                    const newVal = !isNullSelected;
                    setIsNullSelected(newVal);
                    setBoolValue(undefined); // mutually exclusive with Yes/No
                    if (onPendingChange) {
                      onPendingChange(
                        fieldKey,
                        newVal ? { is_null: true } : null,
                      );
                    }
                    if (!newVal) onClear(fieldKey);
                  }}
                />
                Not Available
              </label>
            </div>
          )}
        </div>
        <div className="filter-actions-checkbox">
          <button
            className="apply-btn"
            onClick={handleApply}
            disabled={!hasAnyValue}
          >
            Apply
          </button>
          <button
            className="clear-btn"
            onClick={handleClear}
            disabled={!hasAnyValue}
          >
            Clear
          </button>
        </div>
      </>,
    );
  }

  // Updated Date
  if (fieldType === "dateon") {
    return renderPortaledPopup(
      "inline-filter-popup-date",
      <>
        <div
          className="filter-field"
          onClick={(e) => {
            if (e.target.tagName === "INPUT") {
              const icon = e.currentTarget.querySelector(".calendarIcon");
              if (icon) {
                icon.dispatchEvent(new MouseEvent("click", { bubbles: true }));
              }
            }
          }}
        >
          <label>Last Updated On:</label>
          <CustomDatePickerInput
            value={onDisplay}
            name="inline_updated_date_on"
            handleInputChange={handleOnInput}
            handleCalendarChange={handleOnCalendar}
            ref={onRef}
            selectedDate={selectedOn}
            portalCalendar
          />
        </div>
        {dateRangeError && (
          <div style={{ color: "red", fontSize: "12px", marginBottom: "6px" }}>
            {dateRangeError}
          </div>
        )}
        <div className="filter-actions">
          <button
            className="apply-btn"
            onClick={handleApply}
            disabled={!hasAnyValue}
          >
            Apply
          </button>
          <button
            className="clear-btn"
            onClick={handleClear}
            disabled={!hasAnyValue}
          >
            Clear
          </button>
        </div>
      </>,
    );
  }

  // ── Date range mode ───
  if (fieldType === "daterange") {
    return renderPortaledPopup(
      "inline-filter-popup-date",
      <>
        {/* ── From / To range ── */}
        <div
          className="filter-field"
          onClick={(e) => {
            if (e.target.tagName === "INPUT") {
              const icon = e.currentTarget.querySelector(".calendarIcon");
              if (icon) {
                icon.dispatchEvent(new MouseEvent("click", { bubbles: true }));
              }
            }
          }}
        >
          <label>From:</label>
          <CustomDatePickerInput
            value={fromDisplay}
            name="inline_created_date_from"
            handleInputChange={handleFromInput}
            handleCalendarChange={handleFromCalendar}
            ref={fromRef}
            selectedDate={selectedFrom}
            portalCalendar
          />
        </div>
        <div
          className="filter-field"
          onClick={(e) => {
            if (e.target.tagName === "INPUT") {
              const icon = e.currentTarget.querySelector(".calendarIcon");
              if (icon) {
                icon.dispatchEvent(new MouseEvent("click", { bubbles: true }));
              }
            }
          }}
        >
          <label>To:</label>
          <CustomDatePickerInput
            value={toDisplay}
            name="inline_created_date_to"
            handleInputChange={handleToInput}
            handleCalendarChange={handleToCalendar}
            ref={toRef}
            selectedDate={selectedTo}
            minDate={selectedFrom}
            portalCalendar
          />
        </div>
        {!hideExactOn && (
          <>
            <div
              style={{
                fontSize: "12px",
                color: "#888",
                marginTop: "20px",
                marginBottom: "10px",
                borderTop: "1.5px solid black",
                paddingBottom: "6px",
              }}
            ></div>

            {/* ── Created On (exact date, clears From/To) ── */}
            <div
              className="filter-field"
              onClick={(e) => {
                if (e.target.tagName === "INPUT") {
                  const icon = e.currentTarget.querySelector(".calendarIcon");
                  if (icon) {
                    icon.dispatchEvent(
                      new MouseEvent("click", { bubbles: true }),
                    );
                  }
                }
              }}
            >
              <label>Created On:</label>
              <CustomDatePickerInput
                value={onDisplay}
                name="inline_created_date_on"
                handleInputChange={handleOnInput}
                handleCalendarChange={handleOnCalendar}
                ref={onRef}
                selectedDate={selectedOn}
                portalCalendar
              />
            </div>
          </>
        )}

        {showHasDateBoolean && (
          <div className="filter-has-date-row">
            <label className="filter-has-date-label">Has Purchase Date:</label>
            <div className="filter-field-checkbox filter-has-date-checks">
              {[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ].map(({ label, value }) => (
                <div className="filter-field" key={label} style={{ marginBottom: 0 }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      cursor: "pointer",
                      marginBottom: 0,
                      fontSize: "12px",
                      fontWeight: 600,
                      color: "#333",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={hasDateValue === value}
                      onChange={() => {
                        const newVal =
                          hasDateValue === value ? undefined : value;
                        setHasDateValue(newVal);
                        hasUserEdited.current = true;
                        if (onPendingChange) {
                          const pending = {};
                          if (fromISO) pending.from = fromISO;
                          if (toISO) pending.to = toISO;
                          if (newVal !== undefined) pending.has_date = newVal;
                          onPendingChange(
                            fieldKey,
                            Object.keys(pending).length ? pending : null,
                          );
                        }
                        // NEW: nothing left in this filter → clear it
                        if (newVal === undefined && !fromISO && !toISO) {
                          onClear(fieldKey);
                        }
                      }}
                    />
                    {label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {dateRangeError && (
          <div style={{ color: "red", fontSize: "12px", marginBottom: "6px" }}>
            {dateRangeError}
          </div>
        )}
        <div
          className={
            showHasDateBoolean
              ? "filter-actions filter-actions-spaced"
              : "filter-actions"
          }
        >
          <button
            className="apply-btn"
            onClick={handleApply}
            disabled={!hasAnyValue}
          >
            Apply
          </button>
          <button
            className="clear-btn"
            onClick={handleClear}
            disabled={!hasAnyValue}
          >
            Clear
          </button>
        </div>
      </>,
    );
  }

  if (fieldType === "appnumber") {
    return renderPortaledPopup(
      "inline-filter-popup-app-number",
      <>
        <div className="filter-field">
          <label>Contains:</label>
          <input
            type="text"
            placeholder="CALQ000000"
            value={appNumberContains}
            maxLength={10}
            onChange={(e) => {
              hasUserEdited.current = true;
              const val = e.target.value;
              setAppNumberContains(val);
              if (onPendingChange) {
                onPendingChange(
                  fieldKey,
                  val.trim() ? { contains: val.trim() } : null,
                );
              }
              if (!val.trim()) onClear(fieldKey);
            }}
            style={{
              width: "100%",
              padding: "6px 8px",
              fontSize: "13px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              outline: "none",
            }}
          />
        </div>
        <div className="filter-actions">
          <button className="apply-btn" onClick={handleApply} disabled={!hasAnyValue}>
            Apply
          </button>
          <button className="clear-btn" onClick={handleClear} disabled={!hasAnyValue}>
            Clear
          </button>
        </div>
      </>,
    );
  }

  if (fieldType === "numericrange") {
    return renderPortaledPopup(
      "inline-filter-popup",
      <>
        <div className="filter-field">
          <label>Greater Than or Equal:</label>
          <input
            type="text"
            inputMode="decimal"
            value={filters.gte}
            onChange={(e) =>
              handleChange("gte", sanitizeNumericInput(e.target.value))
            }
          />
        </div>
        <div className="filter-field">
          <label>Less Than or Equal:</label>
          <input
            type="text"
            inputMode="decimal"
            value={filters.lte}
            onChange={(e) =>
              handleChange("lte", sanitizeNumericInput(e.target.value))
            }
          />
        </div>
        <div className="filter-actions">
          <button className="apply-btn" onClick={handleApply} disabled={!hasAnyValue}>
            Apply
          </button>
          <button className="clear-btn" onClick={handleClear} disabled={!hasAnyValue}>
            Clear
          </button>
        </div>
      </>,
    );
  }

  if (fieldType === "exactcontains") {
    return renderPortaledPopup(
      "inline-filter-popup",
      <>
        <div className="filter-field">
          <label>Exact Match:</label>
          <input
            type="text"
            inputMode={fieldKey === "partner_loan_number" ? "numeric" : "text"}
            value={filters.exact}
            onChange={(e) =>
              handleChange(
                "exact",
                fieldKey === "partner_loan_number"
                  ? sanitizeDigitsOnly(e.target.value)
                  : e.target.value,
              )
            }
          />
        </div>
        <div className="filter-field">
          <label>Contains:</label>
          <input
            type="text"
            inputMode={fieldKey === "partner_loan_number" ? "numeric" : "text"}
            value={filters.contains}
            onChange={(e) =>
              handleChange(
                "contains",
                fieldKey === "partner_loan_number"
                  ? sanitizeDigitsOnly(e.target.value)
                  : e.target.value,
              )
            }
          />
        </div>
        <div className="filter-actions">
          <button className="apply-btn" onClick={handleApply} disabled={!hasAnyValue}>
            Apply
          </button>
          <button className="clear-btn" onClick={handleClear} disabled={!hasAnyValue}>
            Clear
          </button>
        </div>
      </>,
    );
  }

  // ── Text mode───
  const showEndswith =
    textFilterVariant === "full" ||
    isBorrowerField ||
    isCxSpecialistField ||
    isStatusField;

  return renderPortaledPopup(
    "inline-filter-popup",
    <>
      <div className="filter-field">
        <label>Contains:</label>
        <input
          type="text"
          value={filters.contains}
          onChange={(e) => handleChange("contains", e.target.value)}
        />
      </div>
      <div className="filter-field">
        <label>Starts With:</label>
        <input
          type="text"
          value={filters.startswith}
          onChange={(e) => handleChange("startswith", e.target.value)}
        />
      </div>
      {showEndswith && (
        <div className="filter-field">
          <label>Ends With:</label>
          <input
            type="text"
            value={filters.endswith}
            onChange={(e) => handleChange("endswith", e.target.value)}
          />
        </div>
      )}
      {isAddressField && (
        <div className="filter-field">
          <label>Zip Code:</label>
          <input
            type="text"
            inputMode="numeric"
            value={filters.zip_code}
            onChange={(e) =>
              handleChange("zip_code", sanitizeDigitsOnly(e.target.value))
            }
          />
        </div>
      )}
      <div className="filter-actions">
        <button className="apply-btn" onClick={handleApply} disabled={!hasAnyValue}>
          Apply
        </button>
        <button className="clear-btn" onClick={handleClear} disabled={!hasAnyValue}>
          Clear
        </button>
      </div>
    </>,
  );
};

export default InlineFilterPopup;
